import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchProducts,
    setFilters,
    resetFilters,
    resetAllFilters,
    deleteProduct,
    clearDeleteError
} from '../features/product/productListSlice';
import {
    updateProductStatus,
    clearStatusState
} from '../features/product/productStatusSlice';
import Styles from '../assets/css/productList.module.scss';
import { AiOutlineSearch } from "react-icons/ai";
import { RiFilter2Line } from "react-icons/ri";
import { IoMdClose } from "react-icons/io";
import { BsThreeDotsVertical } from "react-icons/bs";
import { BiEdit } from "react-icons/bi";
import { BiTrash } from "react-icons/bi";
import { LiaEyeSolid } from "react-icons/lia";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useNavigate, useLocation } from 'react-router-dom';
import Pagination from '../components/common/Pagination';
import Loader from '../utils/Loader';
import ItemImage from '../assets/images/itemImage.png';
import DeleteConfirmation from '../components/common/DeleteConfirmation';
import { popUpBoxTrigger } from '../features/message/popUpSlice';

const ProductList = ({ companyId, companyName, filterByCompany = false, navigateFrom, activeTab = "company" }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { products, loading, error, pagination, filters, deleteLoading, deleteError } = useSelector((state) => state.products);
    const { loading: statusLoading, error: statusError, success: statusSuccess } = useSelector((state) => state.productStatus);

    const [isOpen, setIsOpen] = useState(false);
    const [filterDate, setFilterDate] = useState({
        from: '',
        to: ''
    });
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [expandedProducts, setExpandedProducts] = useState({});
    const dropdownRefs = useRef({});
    const filterDropdownRef = useRef(null);
    const filterButtonRef = useRef(null);
    const [dateError, setDateError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [localProductStatus, setLocalProductStatus] = useState({});
    const [localSKUStatus, setLocalSKUStatus] = useState({});
    const [successMessage, setSuccessMessage] = useState('');

    // Delete confirmation state
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        productId: null,
        productName: '',
        lazadaItemId: '',
        skuIds: []
    });

    // Reset all filters when navigating between different modules
    useEffect(() => {
        dispatch(resetAllFilters());
        return () => {
            dispatch(resetAllFilters());
        };
    }, [dispatch, location.pathname]);

    // Set company filter on mount if filterByCompany is true
    useEffect(() => {
        if (filterByCompany && companyId) {
            dispatch(setFilters({ company_id: companyId }));
        }
    }, [dispatch, companyId, filterByCompany]);

    // Fetch products whenever filters change
    useEffect(() => {
        dispatch(fetchProducts());
    }, [dispatch, filters]);

    // Handle status update responses
    useEffect(() => {
        if (statusSuccess) {
            dispatch(popUpBoxTrigger({
                type: "success",
                heading: "Status Updated",
                message: "Product status updated successfully.",
                show: true
            }));
            dispatch(clearStatusState());
        }

        if (statusError) {
            let errorMessage = "Failed to update product status. Please try again.";
            let errorHeading = "Error";

            if (statusError.message) {
                errorMessage = statusError.message;

                if (statusError.errors) {
                    const fieldErrors = Object.values(statusError.errors).flat();
                    if (fieldErrors.length > 0) {
                        errorMessage = fieldErrors[0];
                    }
                }
            } else if (statusError.status === 422 && statusError.data && statusError.data.length > 0) {
                const firstError = statusError.data[0];
                errorMessage = firstError.message || errorMessage;
                errorHeading = "Update Failed";
            }

            dispatch(popUpBoxTrigger({
                type: "error",
                heading: errorHeading,
                message: errorMessage,
                show: true
            }));

            // Instead of clearing all, you might want to clear only the failed one
            dispatch(clearStatusState());
        }
    }, [statusSuccess, statusError, dispatch]);

    // Handle delete error and success messages
    useEffect(() => {
        if (deleteError) {
            setSuccessMessage(`Error: ${deleteError}`);
            dispatch(clearDeleteError());
        }
    }, [deleteError, dispatch]);

    // Clear success message after 3 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    // Initialize local product status when products are loaded
    useEffect(() => {
        if (products && products.length > 0) {
            const initialStatus = {};
            const initialSKUStatus = {};

            products.forEach(product => {
                // Get all SKU statuses for this product
                const skuStatuses = [];
                if (product.lazada_product?.data?.skus) {
                    product.lazada_product.data.skus.forEach(sku => {
                        const skuKey = `${product.id}_${sku.SkuId}`;
                        const skuStatus = sku.Status === 'active' ? 1 : 0;
                        initialSKUStatus[skuKey] = skuStatus;
                        skuStatuses.push(skuStatus);
                    });
                }

                // Determine main product status based on SKU statuses
                let mainProductStatus;
                if (skuStatuses.length > 0) {
                    const allActive = skuStatuses.every(status => status === 1);
                    const allInactive = skuStatuses.every(status => status === 0);

                    if (allActive) {
                        mainProductStatus = 1;
                    } else if (allInactive) {
                        mainProductStatus = 0;
                    } else {
                        // Mixed status - main product shows active when there's at least one active SKU
                        mainProductStatus = 1;
                    }
                } else {
                    // Fallback to product status or lazada product status
                    mainProductStatus = product.status !== undefined ? product.status :
                        (product.lazada_product?.data?.status === 'Active' ? 1 : 0);
                }

                initialStatus[product.id] = mainProductStatus;
            });

            setLocalProductStatus(initialStatus);
            setLocalSKUStatus(initialSKUStatus);
        }
    }, [products]);

    // Update filtered products whenever products or filters change
    useEffect(() => {
        if (!loading && products) {
            setFilteredProducts(products);
        }
    }, [products, loading]);

    // Check if product has multiple SKUs
    const hasMultipleSKUs = (product) => {
        return product.lazada_product?.data?.skus && product.lazada_product.data.skus.length > 1;
    };

    // Toggle product expansion
    const toggleProductExpansion = (productId, event) => {
        event.stopPropagation();
        setExpandedProducts(prev => ({
            ...prev,
            [productId]: !prev[productId]
        }));
    };

    // Get first SKU image or fallback
    const getSKUImage = (sku, product) => {
        if (sku.Images && sku.Images.length > 0) {
            return sku.Images[0];
        }
        if (product.lazada_product?.data?.images && product.lazada_product.data.images.length > 0) {
            return product.lazada_product.data.images[0];
        }
        return ItemImage;
    };

    // Get display name from sale properties
    const getSKUDisplayName = (sku) => {
        if (sku.saleProp) {
            const values = Object.values(sku.saleProp);
            return values.join(', ');
        }
        return 'Variant';
    };

    // Update main product status
    const handleProductStatusUpdate = async (productId, newStatus, event) => {
        event.stopPropagation();

        const product = products.find(p => p.id === productId);
        if (!product) return;

        try {
            // For main product, use the first SKU ID from the skus array
            const firstSkuId = product.lazada_product?.data?.skus?.[0]?.SkuId;

            const updateData = {
                id: productId.toString(),
                lazada_product_id: product.lazada_item_id?.toString(),
                sku_id: firstSkuId ? firstSkuId.toString() : product.lazada_item_id?.toString(),
                status: newStatus === 1 ? 'active' : 'inactive',
                productId: productId,
                skuId: null,
                type: "all"
            };

            await dispatch(updateProductStatus(updateData)).unwrap();

            // Update ALL SKUs status for this product to match main product status
            if (product.lazada_product?.data?.skus) {
                setLocalSKUStatus(prev => {
                    const updated = { ...prev };
                    product.lazada_product.data.skus.forEach(sku => {
                        const skuKey = `${productId}_${sku.SkuId}`;
                        updated[skuKey] = newStatus;
                    });
                    return updated;
                });
            }

            // Update main product status
            setLocalProductStatus(prev => ({
                ...prev,
                [productId]: newStatus
            }));

        } catch (error) {
            console.log('❌ Main Product Status Update Failed:', error);
        }
    };

    const updateMainProductStatus = (productId, currentSKUStatus) => {
        const product = products.find(p => p.id === productId);
        if (!product || !product.lazada_product?.data?.skus) return;

        const skuStatuses = [];
        product.lazada_product.data.skus.forEach(sku => {
            const skuKey = `${productId}_${sku.SkuId}`;
            // Use the provided currentSKUStatus or fallback to localSKUStatus
            const status = currentSKUStatus ? currentSKUStatus[skuKey] : localSKUStatus[skuKey];
            if (status !== undefined) {
                skuStatuses.push(status);
            }
        });

        // Determine new main product status
        let newMainStatus;
        if (skuStatuses.length > 0) {
            const allActive = skuStatuses.every(status => status === 1);
            const allInactive = skuStatuses.every(status => status === 0);

            if (allActive) {
                newMainStatus = 1;
            } else if (allInactive) {
                newMainStatus = 0;
            } else {
                // Mixed status - main product shows active when there's at least one active SKU
                newMainStatus = 1;
            }
        } else {
            // Keep current status if no SKUs
            newMainStatus = localProductStatus[productId];
        }

        // Update main product status only if changed
        if (localProductStatus[productId] !== newMainStatus) {
            setLocalProductStatus(prev => ({
                ...prev,
                [productId]: newMainStatus
            }));
        }
    };

    // Update SKU status
    const handleSKUStatusUpdate = async (productId, skuId, newStatus, event) => {
        event.stopPropagation();

        const product = products.find(p => p.id === productId);
        if (!product) return;

        try {
            const updateData = {
                id: productId.toString(),
                lazada_product_id: product.lazada_item_id?.toString(),
                sku_id: skuId.toString(),
                status: newStatus === 1 ? 'active' : 'inactive',
                productId: productId,
                skuId: skuId,
                type: "normal"
            };

            await dispatch(updateProductStatus(updateData)).unwrap();

            // Update only this specific SKU status
            const skuKey = `${productId}_${skuId}`;
            setLocalSKUStatus(prev => {
                const updated = {
                    ...prev,
                    [skuKey]: newStatus
                };

                // Auto-update main product status based on all SKU statuses
                updateMainProductStatus(productId, updated);
                return updated;
            });

        } catch (error) {
            console.log('❌ SKU Status Update Failed:', error);
        }
    };

    // Toggle product status (client-side only)
    const toggleProductStatus = (productId, event) => {
        event.stopPropagation();
        const currentStatus = localProductStatus[productId];
        const newStatus = currentStatus === 1 ? 0 : 1;
        handleProductStatusUpdate(productId, newStatus, event);
    };

    // Toggle SKU status
    const toggleSKUStatus = (productId, skuId, event) => {
        event.stopPropagation();
        const skuKey = `${productId}_${skuId}`;
        const currentStatus = localSKUStatus[skuKey];
        const newStatus = currentStatus === 1 ? 0 : 1;
        handleSKUStatusUpdate(productId, skuId, newStatus, event);
    };

    const dateStatusApply = () => {
        if ((filterDate.from && !filterDate.to) || (!filterDate.from && filterDate.to)) {
            setDateError("Please select both From and To dates");
            return;
        }

        if (filterDate.from && filterDate.to && new Date(filterDate.from) > new Date(filterDate.to)) {
            setDateError("From date cannot be after To date");
            return;
        }

        // Keep company_id filter when applying date filters if applicable
        const dateFilters = filterByCompany && companyId
            ? {
                from: filterDate.from,
                to: filterDate.to,
                company_id: companyId,
                page: "1"
            }
            : {
                from: filterDate.from,
                to: filterDate.to,
                page: "1"
            };

        dispatch(setFilters(dateFilters));
        setIsOpen(false);
    };

    const handleClear = () => {
        setFilterDate({ from: '', to: '' });
        setDateError('');
    };

    const handleReset = () => {
        setFilterDate({ from: '', to: '' });
        setSearchTerm('');
        setDateError('');
    };

    // Extract SKU IDs from product data - Get SkuId from lazada data
    const extractSkuIds = (product) => {
        const skuIds = [];

        // Extract SkuId from lazada_product.data.skus if available
        if (product.lazada_product?.data?.skus) {
            product.lazada_product.data.skus.forEach(sku => {
                if (sku.SkuId) {
                    skuIds.push(sku.SkuId.toString());
                }
            });
        }

        // If no SKUs found in lazada data, try suspendedSkus
        if (skuIds.length === 0 && product.lazada_product?.data?.suspendedSkus) {
            product.lazada_product.data.suspendedSkus.forEach(sku => {
                if (sku.SkuId) {
                    skuIds.push(sku.SkuId.toString());
                }
            });
        }

        // If still no SKUs found, use lazada_item_id as fallback
        if (skuIds.length === 0) {
            if (product.lazada_item_id) {
                skuIds.push(product.lazada_item_id.toString());
            } else {
                // Last resort fallback
                skuIds.push(product.id.toString());
            }
        }

        return skuIds;
    };

    const calculateTotalQuantity = (product) => {
        if (!product.lazada_product?.data?.skus) return 0;

        return product.lazada_product.data.skus.reduce((total, sku) => {
            // Use multiWarehouseInventories totalQuantity if available, otherwise use quantity
            const skuQuantity = sku.quantity || 0;
            return total + skuQuantity;
        }, 0);
    };

    // Delete product handler
    const handleDeleteClick = (product, event) => {
        event.stopPropagation();
        setActiveDropdown(null);

        // Extract SKU IDs from product data
        const skuIds = extractSkuIds(product);

        // Get the item_id from lazada_product data or product data
        const itemId = product.lazada_product?.data?.item_id || product.lazada_item_id;

        setDeleteModal({
            isOpen: true,
            productId: product.id,
            productName: product.product_name,
            lazadaItemId: itemId?.toString(),
            skuIds: skuIds
        });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteModal.productId) return;

        try {
            const deleteData = {
                id: deleteModal.productId.toString(),
                productId: deleteModal.productId,
                item_id: deleteModal.lazadaItemId,
                sku_id_list: deleteModal.skuIds
            };

            await dispatch(deleteProduct(deleteData)).unwrap();
            dispatch(popUpBoxTrigger({ type: "success", heading: "Product Deleted", message: "Product deleted successfully.", show: true }));

            // Close the modal
            setDeleteModal({
                isOpen: false,
                productId: null,
                productName: '',
                lazadaItemId: '',
                skuIds: []
            });

        } catch (error) {
            dispatch(popUpBoxTrigger({ type: "error", heading: "Error", message: "Failed to delete product. Please try again.", show: true }));

            setDeleteModal({
                isOpen: false,
                productId: null,
                productName: '',
                lazadaItemId: '',
                skuIds: []
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteModal({
            isOpen: false,
            productId: null,
            productName: '',
            lazadaItemId: '',
            skuIds: []
        });
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (window.searchTimeout) {
            clearTimeout(window.searchTimeout);
        }

        window.searchTimeout = setTimeout(() => {
            const searchFilters = filterByCompany && companyId
                ? { search: value, company_id: companyId, page: "1" }
                : { search: value, page: "1" };

            dispatch(setFilters(searchFilters));
        }, 500);
    };

    const handleDateChange = (type, value) => {
        setFilterDate(prev => ({
            ...prev,
            [type]: value
        }));
        setDateError('');
    };

    const changePage = (count, type) => {
        const pageFilters = filterByCompany && companyId
            ? { company_id: companyId }
            : {};

        if (type === "page") {
            dispatch(setFilters({
                ...pageFilters,
                page: count.toString()
            }));
        } else if (type === "show") {
            dispatch(setFilters({
                ...pageFilters,
                page: "1",
                paginate_per_page: count.toString()
            }));
        }
    };

    const handleDropDown = (productId, event) => {
        event.stopPropagation();
        setActiveDropdown(activeDropdown === productId ? null : productId);
    };

    const handleView = (id, event) => {
        event.stopPropagation();
        setActiveDropdown(null);

        const product = products.find(p => p.id === id);
        if (!product) return;

        // Check if product is active
        const productStatus = localProductStatus[id];
        if (productStatus === 0) {
            dispatch(popUpBoxTrigger({
                type: "error",
                heading: "Product Inactive",
                message: "Cannot view inactive product. Please activate the product first.",
                show: true
            }));
            return;
        }

        // Determine the back route based on where we're navigating from
        let backRoute = "/products"; // Default

        if (navigateFrom === "companyView" && companyId) {
            backRoute = `/company/view/${companyId}`;
        } else if (navigateFrom === "companyEdit" && companyId) {
            backRoute = `/company/edit/${companyId}`;
        } else if (location.pathname.includes("/company/view/")) {
            const match = location.pathname.match(/\/company\/view\/(\d+)/);
            if (match) {
                backRoute = `/company/view/${match[1]}`;
            }
        } else if (location.pathname.includes("/company/edit/")) {
            const match = location.pathname.match(/\/company\/edit\/(\d+)/);
            if (match) {
                backRoute = `/company/edit/${match[1]}`;
            }
        }

        navigate(`/products/view/${id}`, {
            state: {
                from: backRoute,
                activeTab: activeTab
            }
        });
    };

    const handleEdit = (id, event) => {
        event.stopPropagation();
        setActiveDropdown(null);

        const product = products.find(p => p.id === id);
        if (!product) return;

        // Check if product is active
        const productStatus = localProductStatus[id];
        if (productStatus === 0) {
            dispatch(popUpBoxTrigger({
                type: "error",
                heading: "Product Inactive",
                message: "Cannot edit inactive product. Please activate the product first.",
                show: true
            }));
            return;
        }

        // Determine the back route based on where we're navigating from
        let backRoute = "/products"; // Default

        if (navigateFrom === "companyView" && companyId) {
            backRoute = `/company/view/${companyId}`;
        } else if (navigateFrom === "companyEdit" && companyId) {
            backRoute = `/company/edit/${companyId}`;
        } else if (location.pathname.includes("/company/view/")) {
            const match = location.pathname.match(/\/company\/view\/(\d+)/);
            if (match) {
                backRoute = `/company/view/${match[1]}`;
            }
        } else if (location.pathname.includes("/company/edit/")) {
            const match = location.pathname.match(/\/company\/edit\/(\d+)/);
            if (match) {
                backRoute = `/company/edit/${match[1]}`;
            }
        }

        navigate(`/products/edit/${id}`, {
            state: {
                from: backRoute,
                activeTab: activeTab
            }
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '/');
    };

    const StatusToggle = ({ productId, status }) => {
        const { loading: statusLoading } = useSelector((state) => state.productStatus);

        // For main product, use only productId as key (no SKU ID)
        const isLoading = statusLoading[productId.toString()];
        const isActive = localProductStatus[productId] === 1;

        return (
            <div
                className={`${Styles.statusToggle} ${isActive ? Styles.active : Styles.inactive} ${isLoading ? Styles.loading : ''}`}
                onClick={(e) => !isLoading && toggleProductStatus(productId, e)}
            >
                <div className={`${Styles.toggleSlider} ${isActive ? Styles.active : ''}`}>
                    {isLoading ? (
                        <div className={Styles.loaderSpinner}></div>
                    ) : (
                        <span className={Styles.toggleText}>
                            {isActive ? 'Active' : 'Inactive'}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const SKUStatusToggle = ({ productId, skuId, status }) => {
        const { loading: statusLoading } = useSelector((state) => state.productStatus);

        // For SKU, use combination of productId and skuId as key
        const loadingKey = `${productId}_${skuId}`;
        const isLoading = statusLoading[loadingKey];
        const skuKey = `${productId}_${skuId}`;
        const isActive = localSKUStatus[skuKey] === 1;

        return (
            <div
                className={`${Styles.statusToggle} ${isActive ? Styles.active : Styles.inactive} ${isLoading ? Styles.loading : ''}`}
                onClick={(e) => !isLoading && toggleSKUStatus(productId, skuId, e)}
            >
                <div className={`${Styles.toggleSlider} ${isActive ? Styles.active : ''}`}>
                    {isLoading ? (
                        <div className={Styles.loaderSpinner}></div>
                    ) : (
                        <span className={Styles.toggleText}>
                            {isActive ? 'Active' : 'Inactive'}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            const isDropdownClick = event.target.closest(`.${Styles.manage}`) ||
                event.target.closest(`.${Styles.manageUser}`);

            if (!isDropdownClick) {
                setActiveDropdown(null);
            }

            if (isOpen) {
                const isFilterDropdownClick = filterDropdownRef.current && filterDropdownRef.current.contains(event.target);
                const isFilterButtonClick = filterButtonRef.current && filterButtonRef.current.contains(event.target);
                const isDatePickerClick = event.target.type === 'date' ||
                    event.target.closest('input[type="date"]') ||
                    event.target.closest('.react-datepicker') ||
                    event.target.closest('[data-testid="date-picker"]') ||
                    event.target.closest('.datepicker') ||
                    event.target.closest('.date-picker') ||
                    event.target.closest('.calendar') ||
                    event.target.closest('[role="dialog"]') ||
                    event.target.closest('[role="listbox"]') ||
                    event.target.tagName === 'OPTION' ||
                    (event.target.tagName === 'INPUT' && event.target.type === 'date');

                if (!isFilterDropdownClick && !isFilterButtonClick && !isDatePickerClick) {
                    setIsOpen(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Sync local state with filters
    useEffect(() => {
        if (filters.search !== searchTerm) {
            setSearchTerm(filters.search || '');
        }

        if (filters.from !== filterDate.from || filters.to !== filterDate.to) {
            setFilterDate({
                from: filters.from || '',
                to: filters.to || ''
            });
        }
    }, [filters]);

    if (error) {
        return <div className={`${Styles.error}`}>Error: {error}</div>;
    }

    return (
        <div className={`${Styles.container}`}>
            {/* Success/Error Message */}
            {successMessage && (
                <div className={`${Styles.messageBanner} ${successMessage.includes('Error') || successMessage.includes('Failed')
                    ? Styles.error
                    : Styles.success
                    }`}>
                    <span>{successMessage}</span>
                    <button
                        onClick={() => setSuccessMessage('')}
                        className={Styles.closeMessage}
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Filter And Search Data */}
            <div className={`${Styles.filter_search_btn}`}>
                <div className={`${Styles.filter}`}>
                    <div className={`${Styles.sub_filter}`}>
                        <button
                            ref={filterButtonRef}
                            onClick={() => { setIsOpen(!isOpen); }}
                            aria-expanded={isOpen}
                        >
                            <RiFilter2Line /> Filter
                        </button>
                        {isOpen && (
                            <div className={`${Styles.filter_dropdown}`} ref={filterDropdownRef}>
                                <div className={`${Styles.heading}`}>
                                    <p>Filter</p>
                                    <IoMdClose onClick={() => setIsOpen(!isOpen)} />
                                </div>

                                <div className={`${Styles.selectDate}`}>
                                    <div className={`${Styles.heading_clear}`}>
                                        <p>Select Date Range</p>
                                        <p onClick={handleClear}>Clear</p>
                                    </div>
                                    <div className={`${Styles.filter_date}`}>
                                        <div className={`${Styles.from}`}>
                                            <label htmlFor="from">From:</label>
                                            <input
                                                type="date"
                                                id='from'
                                                placeholder='00-00-0000'
                                                value={filterDate.from}
                                                max={filterDate.to || undefined}
                                                onChange={(e) => handleDateChange('from', e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className={`${Styles.to}`}>
                                            <label htmlFor="to">To:</label>
                                            <input
                                                type="date"
                                                id='to'
                                                placeholder='00-00-0000'
                                                value={filterDate.to}
                                                min={filterDate.from || undefined}
                                                onChange={(e) => handleDateChange('to', e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    {dateError && <div className={`${Styles.date_error}`} style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{dateError}</div>}
                                </div>

                                <div className={`${Styles.buttons}`}>
                                    <button onClick={handleReset}>Reset</button>
                                    <button onClick={dateStatusApply}>Apply Now</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className={`${Styles.search}`}>
                    <div className={`${Styles.inputContainer}`}>
                        <AiOutlineSearch className={`${Styles.searchIcon}`} />
                        <input
                            type="text"
                            placeholder="Search by Product Name"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </div>
            </div>

            {/* Item Table */}
            <div className={`${Styles.product}`}>
                <div className={`${Styles.item_table}`}>
                    {loading ? (
                        <div className={`${Styles.loader_container}`}>
                            <Loader size="large" color="primary" />
                        </div>
                    ) : error ? (
                        <div className={Styles.error_message}>
                            {error}
                        </div>
                    ) : filteredProducts.length === 0 ? (
                        <div className={`${Styles.no_data}`}>
                            {filterByCompany
                                ? `No products found for ${companyName || 'this company'}`
                                : "No products found"}
                        </div>
                    ) : (
                        <table>
                            <thead>
                                <tr>
                                    <th className={`${Styles.first_row}`}>No.</th>
                                    <th>Product name</th>
                                    <th>Lazada Product ID</th>
                                    <th>Brand name</th>
                                    <th>Quantity</th>
                                    <th>Status</th>
                                    <th>Created Date</th>
                                    <th aria-label="Actions"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProducts.map((item, index) => (
                                    <React.Fragment key={item.id || index}>
                                        {/* Main Product Row */}
                                        <tr
                                            className={`${hasMultipleSKUs(item) ? Styles.expandableRow : ''} ${expandedProducts[item.id] ? Styles.expanded : ''}`}
                                            onClick={hasMultipleSKUs(item) ? (e) => toggleProductExpansion(item.id, e) : undefined}
                                        >
                                            {/* Reverse numbering: latest = highest number */}
                                            {/* <td className={`${Styles.first_row}`}>
                                                {filteredProducts.length - index}
                                            </td> */}
                                            <td className={`${Styles.first_row}`}>
                                                {(pagination.currentPage - 1) * pagination.perPage + index + 1}
                                            </td>
                                            {/* {[...filteredProducts].reverse().map((item, index) => (
                                    <React.Fragment key={item.id || index}>
                                        <tr
                                            className={`${hasMultipleSKUs(item) ? Styles.expandableRow : ''} ${expandedProducts[item.id] ? Styles.expanded : ''}`}
                                            onClick={hasMultipleSKUs(item) ? (e) => toggleProductExpansion(item.id, e) : undefined}
                                        >
                                            <td className={Styles.first_row}>{index + 1}</td> */}
                                            <td>
                                                <div className={`${Styles.item_name}`}>
                                                    <img
                                                        src={item.product_image?.[0]?.image_url}
                                                        alt={item.product_name}
                                                        className={`${Styles.item_icon}`}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = ItemImage;
                                                        }}
                                                    />
                                                    <span className={`${Styles.product_name_text}`}>
                                                        {item.product_name}
                                                    </span>
                                                    {hasMultipleSKUs(item) && (
                                                        <span className={Styles.expandIcon}>
                                                            {expandedProducts[item.id] ? <FiChevronUp /> : <FiChevronDown />}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>{item.lazada_item_id || 'N/A'}</td>
                                            <td>{item.lazada_product.data?.attributes?.brand}</td>
                                            <td>{calculateTotalQuantity(item)}</td>
                                            <td>
                                                <StatusToggle
                                                    productId={item.id}
                                                    status={localProductStatus[item.id]}
                                                // isLoading={statusLoading}
                                                />
                                            </td>
                                            <td>{formatDate(item.created_at)}</td>
                                            <td>
                                                <div className={`${Styles.manage}`}
                                                    ref={el => dropdownRefs.current[item.id] = el}>
                                                    <BsThreeDotsVertical
                                                        onClick={(e) => handleDropDown(item.id, e)}
                                                    />
                                                    {activeDropdown === item.id && (
                                                        <div className={`${Styles.manageUser}`}>
                                                            <p onClick={(e) => handleView(item.id, e)}>
                                                                <span><LiaEyeSolid /></span>
                                                                <span>View</span>
                                                            </p>
                                                            <p onClick={(e) => handleEdit(item.id, e)}>
                                                                <span><BiEdit /></span>
                                                                <span>Edit</span>
                                                            </p>
                                                            <p onClick={(e) => handleDeleteClick(item, e)}>
                                                                <span><BiTrash /></span>
                                                                <span>Delete</span>
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>

                                        {/* SKU Rows - Displayed as regular table rows */}
                                        {hasMultipleSKUs(item) && expandedProducts[item.id] && (
                                            item.lazada_product.data.skus.map((sku, index) => (
                                                <tr key={sku.SkuId} className={Styles.skuRow}>
                                                    <td className={Styles.first_row}></td>
                                                    <td>
                                                        <div className={Styles.item_name}>
                                                            <img
                                                                src={getSKUImage(sku, item)}
                                                                alt={getSKUDisplayName(sku)}
                                                                className={Styles.item_icon}
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = ItemImage;
                                                                }}
                                                            />
                                                            <span className={Styles.product_name_text}>
                                                                {getSKUDisplayName(sku)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>{item.lazada_item_id || 'N/A'}</td>
                                                    <td>{item.brand_name}</td>
                                                    <td>{sku.quantity}</td>
                                                    <td>
                                                        <SKUStatusToggle
                                                            productId={item.id}
                                                            skuId={sku.SkuId}
                                                            status={localSKUStatus[`${item.id}_${sku.SkuId}`]}
                                                        // isLoading={statusLoading}
                                                        />
                                                    </td>
                                                    <td>{formatDate(item.created_at)}</td>
                                                    <td></td>
                                                </tr>
                                            ))
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Pagination */}
            {filteredProducts && filteredProducts.length > 0 && (
                <Pagination
                    data={{
                        currentPage: pagination.currentPage,
                        totalPage: pagination.totalPages,
                        showing: pagination.perPage
                    }}
                    pageClick={changePage}
                />
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmation
                isOpen={deleteModal.isOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title="Delete Product"
                message={`Are you sure you want to delete "${deleteModal.productName}"? This action cannot be undone and will remove the product from Lazada.`}
                confirmText="Delete Product"
                cancelText="Cancel"
                isLoading={deleteLoading}
                productData={{
                    product_name: deleteModal.productName,
                    lazada_item_id: deleteModal.lazadaItemId,
                    sku_ids: deleteModal.skuIds
                }}
            />
        </div>
    );
};

export default ProductList;
